// IndexedDB bridge — reads/writes SRS data via AWENI's OfflineManager stores.
import type { AyahCardRecord, DeckCardRecord, SRSStats } from './SRSTypes';
import { apiCardToSRS } from './SRSEngine';

const DB_NAME = 'aweni-offline';
const DB_VERSION = 1;
const STORE_MEMORIZ = 'memoriz_cards';
const STORE_DECK = 'deck_cards';
const STORE_META = 'aweni_meta';
const META_KEY_SYNC = 'hisni_last_sync';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_MEMORIZ)) {
        db.createObjectStore(STORE_MEMORIZ, { keyPath: ['surah', 'ayah'] });
      }
      if (!db.objectStoreNames.contains(STORE_DECK)) {
        db.createObjectStore(STORE_DECK, { keyPath: ['deck_id', 'card_id'] });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  db: IDBDatabase, stores: string | string[],
  mode: IDBTransactionMode,
  fn: (tx: IDBTransaction) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(stores, mode);
    const req = fn(t);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function seedAyahCards(cards: unknown[]): Promise<void> {
  const db = await openDB();
  const t = db.transaction(STORE_MEMORIZ, 'readwrite');
  const store = t.objectStore(STORE_MEMORIZ);
  for (const raw of cards) {
    const c = raw as Record<string, unknown>;
    const record: AyahCardRecord = {
      surah: c.surah_id as number,
      ayah: c.ayah_number as number,
      mastery_level: (c.mastery_level as number) ?? 0,
      error_words: (c.error_words as string[]) ?? [],
      srs: apiCardToSRS(c as Parameters<typeof apiCardToSRS>[0]),
    };
    store.put(record);
  }
  await new Promise<void>((res, rej) => {
    t.oncomplete = () => res();
    t.onerror = () => rej(t.error);
  });
  // Write sync timestamp
  const metaDB = await openDB();
  await tx(metaDB, STORE_META, 'readwrite',
    (t) => t.objectStore(STORE_META).put(new Date().toISOString(), META_KEY_SYNC));
}

export async function getDueAyahCards(now = new Date()): Promise<AyahCardRecord[]> {
  const db = await openDB();
  const all: AyahCardRecord[] = await tx(db, STORE_MEMORIZ, 'readonly',
    (t) => t.objectStore(STORE_MEMORIZ).getAll());
  return all.filter((c) => c.srs.due <= now);
}

export async function computeSRSStats(now = new Date()): Promise<SRSStats> {
  const db = await openDB();
  const cards: AyahCardRecord[] = await tx(db, STORE_MEMORIZ, 'readonly',
    (t) => t.objectStore(STORE_MEMORIZ).getAll());
  const metaDB = await openDB();
  const lastSync = await tx<string | undefined>(metaDB, STORE_META, 'readonly',
    (t) => t.objectStore(STORE_META).get(META_KEY_SYNC));
  return {
    total: cards.length,
    dueToday: cards.filter((c) => c.srs.due <= now).length,
    mastered: cards.filter((c) => c.srs.reps >= 4).length,
    lastSync: lastSync ?? null,
  };
}
